import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../context/StoreContext.tsx';
import { generateChipImage } from '../services/geminiService.ts';
import { FORMATS } from '../constants.ts';
import { AssetType, AssetStatus, Campaign } from '../types.ts';
import { Sparkles, Loader2, Save, Download, RefreshCw, Key, AlertCircle, Image as ImageIcon, Upload, X, Plus, Shirt, Stamp, FolderPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QuickGenerate: React.FC = () => {
  const { dna, addAsset, campaigns, addCampaign, scenarios, environments, attire, addScenario, addEnvironment, addAttire, addNotification } = useStore();
  const navigate = useNavigate();
  
  // State Initialization (Safeguard against empty lists if loading)
  // We use useEffect to set initial selection once data is loaded
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [selectedEnv, setSelectedEnv] = useState<string>('');
  const [selectedAttire, setSelectedAttire] = useState<string>('');

  // Creation State
  const [isAddingScenario, setIsAddingScenario] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  
  const [isAddingEnv, setIsAddingEnv] = useState(false);
  const [newEnvName, setNewEnvName] = useState('');
  const [envReferenceImage, setEnvReferenceImage] = useState<string | null>(null);

  const [isAddingAttire, setIsAddingAttire] = useState(false);
  const [newAttireName, setNewAttireName] = useState('');

  // Additional Context
  const [additionalContext, setAdditionalContext] = useState('');

  const [selectedFormat, setSelectedFormat] = useState(FORMATS[0].id);
  const [styleLevel, setStyleLevel] = useState(5);
  const [isHighQuality, setIsHighQuality] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [selectedResult, setSelectedResult] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Reference Image State
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [useReference, setUseReference] = useState(true);

  // Brand/Logo Image State
  const [brandLogo, setBrandLogo] = useState<string | null>(null);

  // Save Modal State
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [selectedSaveCampaign, setSelectedSaveCampaign] = useState<string>('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  
  // Loading state for saving to prevent duplicates
  const [isSaving, setIsSaving] = useState(false);

  // Initialize selections when data loads
  useEffect(() => {
    if (scenarios.length > 0 && !selectedScenario) setSelectedScenario(scenarios[0].id);
    if (environments.length > 0 && !selectedEnv) setSelectedEnv(environments[0].id);
    if (attire.length > 0 && !selectedAttire) setSelectedAttire(attire[0].name);
  }, [scenarios, environments, attire]);

  // Load reference from DNA on mount or when DNA changes
  useEffect(() => {
    if (dna.visual.likenessImage) {
      setReferenceImage(dna.visual.likenessImage);
      setUseReference(true);
    }
  }, [dna.visual.likenessImage]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferenceImage(reader.result as string);
        setUseReference(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBrandLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEnvImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEnvReferenceImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearReference = () => {
    setReferenceImage(null);
  };

  const handleAddScenario = async () => {
    if (newScenarioName.trim()) {
      const newId = `custom-scen-${Date.now()}`;
      await addScenario({
        id: newId,
        name: newScenarioName,
        icon: '✨',
        prompt: `Chip ${newScenarioName}`,
        isCustom: true
      });
      setSelectedScenario(newId);
      setNewScenarioName('');
      setIsAddingScenario(false);
    }
  };

  const handleAddEnvironment = async () => {
    if (newEnvName.trim()) {
      const newId = `custom-env-${Date.now()}`;
      await addEnvironment({
        id: newId,
        name: newEnvName,
        isCustom: true
      });
      setSelectedEnv(newId);
      setNewEnvName('');
      setIsAddingEnv(false);
    }
  };

  const handleAddAttire = async () => {
    if (newAttireName.trim()) {
      const newId = `custom-attire-${Date.now()}`;
      await addAttire({
        id: newId,
        name: newAttireName,
        isCustom: true
      });
      setSelectedAttire(newAttireName);
      setNewAttireName('');
      setIsAddingAttire(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGeneratedImages([]);
    setError(null);
    
    const scenario = scenarios.find(s => s.id === selectedScenario);
    const environment = environments.find(e => e.id === selectedEnv);
    const format = FORMATS.find(f => f.id === selectedFormat);
    
    const activeRefImage = useReference ? referenceImage : null;

    try {
      const images = await generateChipImage(
        scenario?.prompt || '',
        environment?.name || 'Studio',
        styleLevel,
        dna,
        isHighQuality,
        activeRefImage,
        selectedAttire,
        format?.ratio || '1:1', // Pass the correct ratio from constants
        brandLogo,
        envReferenceImage,
        additionalContext
      );
      setGeneratedImages(images);
      
      // Notify success
      addNotification('success', 'Image Generated', `Successfully generated Chip ${scenario?.name || 'scene'}.`);

    } catch (err: any) {
      if (err.message === "API_KEY_MISSING") {
        setError("High Quality (Nano Banana Pro) requires a paid API Key.");
        addNotification('error', 'Generation Failed', 'API Key missing for high quality model.');
      } else {
        console.error(err);
        setError("Failed to generate. Please try again.");
        addNotification('error', 'Generation Failed', 'An unexpected error occurred.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectKey = async () => {
    const win = window as any;
    if (win.aistudio?.openSelectKey) {
      await win.aistudio.openSelectKey();
      setError(null);
    }
  };

  const handleSaveClick = () => {
    setIsSaveModalOpen(true);
  };

  const handleConfirmSave = async () => {
    if (generatedImages.length === 0 || isSaving) return;
    
    setIsSaving(true);
    try {
      let campaignIdToUse = selectedSaveCampaign;

      // Create new campaign if requested
      if (isCreatingCampaign && newCampaignName.trim()) {
        const newId = `camp-${Date.now()}`;
        await addCampaign({
          id: newId,
          name: newCampaignName,
          description: 'Created via Visual Studio',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
          status: 'active',
          assetCount: 0
        });
        campaignIdToUse = newId;
      }

      const asset = {
        id: `gen-${Date.now()}`,
        url: generatedImages[selectedResult],
        type: AssetType.Image,
        createdAt: Date.now(),
        scenario: scenarios.find(s => s.id === selectedScenario)?.name || 'Custom',
        format: selectedFormat,
        status: AssetStatus.Draft,
        campaignId: campaignIdToUse || undefined,
        tags: ['generated', selectedScenario],
        meta: {
          env: selectedEnv,
          style: styleLevel,
          hq: isHighQuality,
          attire: selectedAttire,
          context: additionalContext
        }
      };
      
      await addAsset(asset);
      addNotification('success', 'Asset Saved', `Saved to ${campaignIdToUse ? 'Campaign' : 'Library'}`);
      
      setIsSaveModalOpen(false);
      navigate('/library');
    } catch (err) {
      console.error("Save failed", err);
      setError("Failed to save asset. Please try again.");
      addNotification('error', 'Save Failed', 'Could not save asset to library.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-140px)] flex flex-col md:flex-row gap-6 relative">
      {/* Left Panel: Configuration */}
      <div className="w-full md:w-1/3 flex flex-col gap-6 overflow-y-auto pr-2 pb-4 scrollbar-hide">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[var(--text-primary)] mb-1">Visual Studio</h1>
          <p className="text-sm text-[var(--text-secondary)]">Create consistent Chip assets instantly.</p>
        </div>

        {/* Reference Image Control */}
        <section className="bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--border-subtle)]">
           <div className="flex items-center justify-between mb-3">
             <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer">
               <input 
                 type="checkbox" 
                 checked={useReference && !!referenceImage}
                 onChange={(e) => setUseReference(e.target.checked)}
                 disabled={!referenceImage}
                 className="w-4 h-4 text-[var(--text-primary)] rounded border-[var(--border-default)]"
               />
               <ImageIcon size={16} />
               <span>Use Reference Image</span>
             </label>
             {referenceImage && (
               <button onClick={clearReference} className="text-[var(--text-tertiary)] hover:text-red-500">
                 <X size={14} />
               </button>
             )}
           </div>
           
           {referenceImage ? (
             <div className={`relative rounded-lg overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-primary)] aspect-video group ${!useReference ? 'opacity-50 grayscale' : ''}`}>
               <img src={referenceImage} alt="Reference" className="w-full h-full object-cover" />
               <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                 <label className="cursor-pointer bg-white text-black text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg hover:scale-105 transition-transform">
                   <Upload size={12} /> Change
                   <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                 </label>
               </div>
             </div>
           ) : (
             <label className="block w-full border-2 border-dashed border-[var(--border-default)] rounded-lg p-4 text-center cursor-pointer hover:border-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors">
               <Upload size={20} className="mx-auto text-[var(--text-tertiary)] mb-2" />
               <span className="text-xs text-[var(--text-secondary)]">Upload Reference Image</span>
               <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
             </label>
           )}
           <p className="text-[10px] text-[var(--text-tertiary)] mt-2 leading-relaxed">
             Use a clear, front-facing image of Chip to ensure structural consistency.
           </p>
        </section>

        {/* Brand/Logo Upload */}
        <section className="bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--border-subtle)]">
           <div className="flex items-center justify-between mb-3">
             <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
               <Stamp size={16} />
               <span>Brand / Logo</span>
             </label>
             {brandLogo && (
               <button onClick={() => setBrandLogo(null)} className="text-[var(--text-tertiary)] hover:text-red-500">
                 <X size={14} />
               </button>
             )}
           </div>
           
           {brandLogo ? (
             <div className="relative rounded-lg overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-primary)] h-20 group flex items-center justify-center">
               <img src={brandLogo} alt="Logo" className="max-h-full max-w-full object-contain p-2" />
               <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                 <label className="cursor-pointer bg-white text-black text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg hover:scale-105 transition-transform">
                   <Upload size={12} /> Change
                   <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                 </label>
               </div>
             </div>
           ) : (
             <label className="block w-full border-2 border-dashed border-[var(--border-default)] rounded-lg p-3 text-center cursor-pointer hover:border-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors">
               <span className="text-xs text-[var(--text-secondary)] flex items-center justify-center gap-2">
                 <Upload size={14} /> Upload Logo
               </span>
               <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
             </label>
           )}
           <p className="text-[10px] text-[var(--text-tertiary)] mt-2 leading-relaxed">
             Upload a logo to integrate branding onto Chip's attire or background.
           </p>
        </section>

        {/* Scenarios (Compact) */}
        <section>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">Scenario</label>
            <button 
              onClick={() => setIsAddingScenario(!isAddingScenario)} 
              className="text-[10px] uppercase font-bold text-[var(--text-primary)] hover:underline flex items-center gap-1"
            >
              <Plus size={12} /> Add New
            </button>
          </div>

          {isAddingScenario && (
            <div className="flex gap-2 mb-2 animate-[fadeIn_0.2s_ease-out]">
              <input 
                type="text" 
                autoFocus
                value={newScenarioName}
                onChange={(e) => setNewScenarioName(e.target.value)}
                className="input-field py-2 text-sm"
                placeholder="e.g. Chip Skateboarding"
                onKeyDown={(e) => e.key === 'Enter' && handleAddScenario()}
              />
              <button onClick={handleAddScenario} className="btn-primary py-2 px-3 text-sm">Add</button>
              <button onClick={() => setIsAddingScenario(false)} className="btn-ghost py-2 px-3 text-sm"><X size={14}/></button>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            {scenarios.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedScenario(s.id)}
                className={`group p-2 rounded-lg transition-all duration-200 flex flex-col items-center justify-center gap-1 text-center border h-20 ${
                  selectedScenario === s.id
                    ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-transparent shadow-md'
                    : 'bg-[var(--bg-primary)] text-[var(--text-primary)] border-[var(--border-subtle)] hover:border-[var(--text-primary)]'
                }`}
                title={s.name}
              >
                <div className="text-lg leading-none">{s.icon || '✨'}</div>
                <div className="text-[10px] font-medium leading-tight line-clamp-2">{s.name}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Environment & Attire */}
        <div className="grid grid-cols-1 gap-4">
          <section>
            <div className="flex justify-between items-center mb-2">
               <label className="block text-sm font-medium text-[var(--text-primary)]">Environment</label>
               <button 
                onClick={() => setIsAddingEnv(!isAddingEnv)} 
                className="text-[10px] uppercase font-bold text-[var(--text-primary)] hover:underline flex items-center gap-1"
               >
                 <Plus size={12} /> Add New
               </button>
            </div>
            
            {isAddingEnv ? (
               <div className="flex gap-2 mb-2 animate-[fadeIn_0.2s_ease-out]">
                 <input 
                   type="text" 
                   autoFocus
                   value={newEnvName}
                   onChange={(e) => setNewEnvName(e.target.value)}
                   className="input-field py-2 text-sm"
                   placeholder="e.g. Mars Colony"
                   onKeyDown={(e) => e.key === 'Enter' && handleAddEnvironment()}
                 />
                 <button onClick={handleAddEnvironment} className="btn-primary py-2 px-3 text-sm">Add</button>
                 <button onClick={() => setIsAddingEnv(false)} className="btn-ghost py-2 px-3 text-sm"><X size={14}/></button>
               </div>
            ) : null}

            <select 
              value={selectedEnv}
              onChange={(e) => setSelectedEnv(e.target.value)}
              className="input-field"
            >
              {environments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>

            {/* Environment Image Upload */}
            <div className="mt-3">
               {envReferenceImage ? (
                 <div className="relative rounded-lg overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-primary)] h-20 group flex items-center justify-center">
                   <img src={envReferenceImage} alt="Env Ref" className="w-full h-full object-cover" />
                    <button 
                      onClick={() => setEnvReferenceImage(null)}
                      className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full hover:bg-red-500 transition-colors"
                      title="Remove Environment Image"
                    >
                      <X size={12} />
                    </button>
                 </div>
               ) : (
                 <label className="flex items-center gap-2 cursor-pointer p-2 border border-dashed border-[var(--border-default)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors group">
                    <div className="p-1.5 bg-[var(--bg-tertiary)] rounded text-[var(--text-secondary)] group-hover:bg-[var(--bg-primary)]">
                       <ImageIcon size={14} />
                    </div>
                    <span className="text-xs text-[var(--text-secondary)] flex-1">Upload Environment Ref</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleEnvImageUpload} />
                 </label>
               )}
            </div>
          </section>

          <section>
            <div className="flex justify-between items-center mb-2">
               <label className="block text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
                <Shirt size={14} /> Attire
               </label>
               <button 
                onClick={() => setIsAddingAttire(!isAddingAttire)} 
                className="text-[10px] uppercase font-bold text-[var(--text-primary)] hover:underline flex items-center gap-1"
               >
                 <Plus size={12} /> Add New
               </button>
            </div>

            {isAddingAttire ? (
               <div className="flex gap-2 mb-2 animate-[fadeIn_0.2s_ease-out]">
                 <input 
                   type="text" 
                   autoFocus
                   value={newAttireName}
                   onChange={(e) => setNewAttireName(e.target.value)}
                   className="input-field py-2 text-sm"
                   placeholder="e.g. Scuba Gear"
                   onKeyDown={(e) => e.key === 'Enter' && handleAddAttire()}
                 />
                 <button onClick={handleAddAttire} className="btn-primary py-2 px-3 text-sm">Add</button>
                 <button onClick={() => setIsAddingAttire(false)} className="btn-ghost py-2 px-3 text-sm"><X size={14}/></button>
               </div>
            ) : null}

            <select 
              value={selectedAttire}
              onChange={(e) => setSelectedAttire(e.target.value)}
              className="input-field"
            >
              {attire.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </section>

          <section>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Format</label>
            <div className="flex flex-wrap gap-2">
              {FORMATS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setSelectedFormat(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedFormat === f.id
                      ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]'
                      : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] border-[var(--border-default)] hover:border-[var(--border-subtle)]'
                  }`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* Style Controls */}
        <section className="card p-4">
           <div className="flex justify-between items-center mb-2">
             <label className="text-sm font-medium text-[var(--text-primary)]">Expressiveness</label>
             <span className="text-xs text-[var(--text-secondary)]">{styleLevel}/10</span>
           </div>
           <input 
             type="range" 
             min="1" 
             max="10" 
             value={styleLevel}
             onChange={(e) => setStyleLevel(parseInt(e.target.value))}
             className="w-full h-2 bg-[var(--bg-tertiary)] rounded-lg appearance-none cursor-pointer accent-[var(--accent-primary)]"
           />
           <div className="flex justify-between mt-1 text-[10px] text-[var(--text-tertiary)] uppercase font-bold tracking-wider">
             <span>Subtle</span>
             <span>Energetic</span>
           </div>
        </section>
        
        <section className="flex items-center gap-2">
          <input 
            type="checkbox" 
            id="hq" 
            checked={isHighQuality}
            onChange={(e) => setIsHighQuality(e.target.checked)}
            className="w-4 h-4 text-[var(--text-primary)] rounded border-[var(--border-default)] focus:ring-[var(--text-primary)]"
          />
          <label htmlFor="hq" className="text-sm text-[var(--text-primary)]">Use High Quality (Nano Banana Pro)</label>
        </section>

        {error && (
            <div className="p-3 bg-red-500/10 text-red-600 rounded-lg text-sm flex flex-col gap-2 border border-red-500/20">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
              {error.includes("API Key") && (
                <button 
                  onClick={handleSelectKey}
                  className="w-full py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 rounded text-xs font-semibold flex items-center justify-center gap-1 transition-colors"
                >
                  <Key size={14} /> Select Paid API Key
                </button>
              )}
            </div>
          )}

        {/* Additional Context Field */}
        <section>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Additional Context</label>
          <textarea 
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            className="input-field text-sm"
            rows={2}
            placeholder="Add specific details (e.g. 'holding a coffee cup', 'blue lighting')..."
          />
        </section>

        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="mt-2 w-full btn-primary py-4 text-lg shadow-lg flex items-center justify-center gap-2"
        >
          {isGenerating ? <Loader2 className="animate-spin" /> : <Sparkles />}
          {isGenerating ? 'Generating...' : 'Generate Chip'}
        </button>
      </div>

      {/* Right Panel: Results */}
      <div className="flex-1 card p-6 flex flex-col items-center justify-center relative overflow-hidden bg-[var(--bg-tertiary)]">
        {isGenerating ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--bg-secondary)] overflow-hidden">
             {/* Animated Background Gradients */}
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-[radial-gradient(circle_at_center,var(--accent-primary)_0%,transparent_70%)] opacity-5 blur-[100px] animate-[pulse_4s_ease-in-out_infinite]" />
             
             {/* Central Tech Loader */}
             <div className="relative mb-8">
               {/* Outer Ring */}
               <div className="w-24 h-24 rounded-full border border-[var(--border-subtle)] flex items-center justify-center relative">
                  <div className="absolute inset-0 border-t-2 border-[var(--accent-primary)] rounded-full animate-spin" />
                  <div className="absolute inset-1 border-b-2 border-purple-500 rounded-full animate-[spin_2s_linear_infinite_reverse]" />
                  
                  {/* Inner pulsing orb */}
                  <div className="w-12 h-12 bg-[var(--bg-tertiary)] rounded-full flex items-center justify-center shadow-inner relative z-10 animate-pulse">
                     <Sparkles className="text-[var(--text-primary)]" size={20} />
                  </div>
               </div>
               
               {/* Orbiting particles */}
               <div className="absolute top-1/2 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2 animate-[spin_8s_linear_infinite]">
                  <div className="absolute top-0 left-1/2 w-2 h-2 bg-[var(--text-primary)] rounded-full -translate-x-1/2 shadow-[0_0_10px_var(--text-primary)]" />
               </div>
             </div>
             
             {/* Text Content */}
             <div className="text-center relative z-10 space-y-2">
               <h3 className="text-xl font-heading font-bold text-[var(--text-primary)] tracking-tight">
                 Generating Assets
               </h3>
               <div className="flex items-center justify-center gap-1.5">
                 <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_0ms]" />
                 <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_100ms]" />
                 <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_200ms]" />
               </div>
               <p className="text-xs text-[var(--text-secondary)] font-mono opacity-80 mt-4">
                 Processing Chip DNA • Applying Brand Layer
               </p>
             </div>
          </div>
        ) : generatedImages.length > 0 ? (
          <div className="w-full h-full flex flex-col">
            <div className="flex-1 flex items-center justify-center bg-[var(--bg-primary)] rounded-xl shadow-sm overflow-hidden mb-6 relative border border-[var(--border-subtle)]">
              <img 
                src={generatedImages[selectedResult]} 
                alt="Generated Chip" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
            
            <div className="flex items-center justify-between card p-4 bg-[var(--glass-bg)]">
               <div className="flex gap-2 overflow-x-auto">
                 {generatedImages.map((img, idx) => (
                   <button
                    key={idx}
                    onClick={() => setSelectedResult(idx)}
                    className={`w-16 h-16 rounded-lg border-2 overflow-hidden ${selectedResult === idx ? 'border-[var(--text-primary)]' : 'border-transparent'}`}
                   >
                     <img src={img} className="w-full h-full object-cover" alt="" />
                   </button>
                 ))}
               </div>
               <div className="flex gap-2">
                 <button onClick={handleGenerate} className="btn-ghost p-3" title="Regenerate">
                   <RefreshCw size={20} />
                 </button>
                 <button className="btn-ghost p-3" title="Download">
                   <Download size={20} />
                 </button>
                 <button 
                  onClick={handleSaveClick}
                  className="btn-primary flex items-center gap-2"
                 >
                   <Save size={18} /> Save to Library
                 </button>
               </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-[var(--text-tertiary)]">
            <div className="w-24 h-24 bg-[var(--bg-primary)] rounded-full mx-auto mb-4 flex items-center justify-center border border-[var(--border-subtle)]">
              <Sparkles size={40} className="text-[var(--text-tertiary)]" />
            </div>
            <p className="text-lg font-medium text-[var(--text-secondary)]">Ready to create.</p>
            <p className="text-sm">Configure your settings and hit Generate.</p>
          </div>
        )}
      </div>

      {/* Save to Library Modal */}
      {isSaveModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-md border border-[var(--border-subtle)] animate-slideUp overflow-hidden">
             <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h3 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                  <FolderPlus size={18} /> Save Asset
                </h3>
                <button onClick={() => setIsSaveModalOpen(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                  <X size={20} />
                </button>
             </div>
             
             <div className="p-6 space-y-4">
                <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Save to Folder (Campaign)</label>
                   
                   {!isCreatingCampaign ? (
                     <div className="space-y-2">
                       <select 
                         className="input-field"
                         value={selectedSaveCampaign}
                         onChange={(e) => setSelectedSaveCampaign(e.target.value)}
                         disabled={isSaving}
                       >
                         <option value="">Unassigned</option>
                         {campaigns.filter(c => c.status === 'active').map(c => (
                           <option key={c.id} value={c.id}>{c.name}</option>
                         ))}
                       </select>
                       <button 
                         onClick={() => setIsCreatingCampaign(true)}
                         disabled={isSaving}
                         className="text-xs text-[var(--accent-primary)] hover:underline flex items-center gap-1 font-medium"
                       >
                         <Plus size={12} /> Create New Folder
                       </button>
                     </div>
                   ) : (
                      <div className="space-y-2">
                        <input 
                          type="text" 
                          autoFocus
                          placeholder="New Folder Name" 
                          className="input-field"
                          value={newCampaignName}
                          onChange={(e) => setNewCampaignName(e.target.value)}
                          disabled={isSaving}
                        />
                        <button 
                         onClick={() => { setIsCreatingCampaign(false); setNewCampaignName(''); }}
                         disabled={isSaving}
                         className="text-xs text-[var(--text-tertiary)] hover:underline"
                       >
                         Cancel
                       </button>
                      </div>
                   )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border-subtle)]">
                   <button 
                     onClick={() => setIsSaveModalOpen(false)}
                     disabled={isSaving}
                     className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg disabled:opacity-50"
                   >
                     Cancel
                   </button>
                   <button 
                     onClick={handleConfirmSave}
                     disabled={isSaving}
                     className="btn-primary flex items-center gap-2 min-w-[120px] justify-center"
                   >
                     {isSaving ? <Loader2 className="animate-spin" size={16} /> : null}
                     {isSaving ? 'Saving...' : 'Save Asset'}
                   </button>
                </div>
             </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuickGenerate;